import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Shield, AlertCircle } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const AdminLoginPage = () => {
  useSEO({ title: "Admin Login | Ecowallet", noindex: true });
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/admin";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user?.role !== "admin") {
        setError("This account is not an admin. Contact an existing admin for access.");
        return;
      }
      toast.success("Welcome back, admin");
      nav(next, { replace: true });
    } catch (e) {
      const raw = formatApiError(e.response?.data?.detail);
      if ((raw || "").toLowerCase().includes("invalid email or password")) {
        setError("Incorrect admin email or password.");
      } else setError(raw || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-14">
      <div className="w-full max-w-md card-premium p-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-primary text-primary-foreground"><Shield className="h-5 w-5" /></span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Admin sign in</h1>
            <p className="text-xs text-muted-foreground">Secure access to the Ecowallet admin console</p>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 text-red-800 dark:text-red-200 px-4 py-3 text-sm mb-4" data-testid="admin-login-error">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Admin email</label>
            <input required type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary" data-testid="admin-login-email" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Password</label>
            <input required type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary" data-testid="admin-login-password" />
          </div>
          <button type="submit" disabled={loading} className="btn-pill w-full bg-primary text-primary-foreground disabled:opacity-50" data-testid="admin-login-submit">
            {loading ? "Signing in..." : "Sign in as admin"}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-muted-foreground">
          Not an admin? <Link to="/login" className="text-primary font-semibold hover:underline">Sign in as user</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
